import { inject } from "@angular/core";
import { type Route, Router } from "@angular/router";

import { NotArchivedPlan, notExternalOrParticipatingEntity } from "../../../core/guards";
import { AdaaHelper } from "../../../core/utils";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean, PageMode } from "../../../shared/constants/enums";
import { mainObjectiveGuards, objectiveGuards, submenuGuards } from "../_guard";

const oldNewDSRoute: Route[] = [
  {
    path: "create",
    canActivate: [notExternalOrParticipatingEntity, ...objectiveGuards.create, NotArchivedPlan()],
    data: {
      title: "breadcrumbs.objectives.main_create",
      breadcrumb: "breadcrumbs.objectives.main_create",
      pageMode: PageMode.create,
      objectiveType: Constants.OBJECTIVE_TYPE.STRATEGIC_OBJECTIVE,
    },
    loadComponent: () => import("../objectives-editor/objectives-editor").then((m) => m.ObjectivesEditorComponent),
  },
  {
    path: "edit/:id",
    canActivate: [notExternalOrParticipatingEntity, ...objectiveGuards.edit, NotArchivedPlan()],
    data: {
      title: "breadcrumbs.objectives.main_edit",
      breadcrumb: "breadcrumbs.objectives.main_edit",
      pageMode: PageMode.edit,
      objectiveType: Constants.OBJECTIVE_TYPE.STRATEGIC_OBJECTIVE,
    },
    loadComponent: () => import("../objectives-editor/objectives-editor").then((m) => m.ObjectivesEditorComponent),
  },
  {
    path: ":id",
    canActivate: [...objectiveGuards.view],
    data: {
      title: "breadcrumbs.view_objective",
      breadcrumb: "breadcrumbs.view_objective",
      type: Constants.OBJECTIVE_TYPE.STRATEGIC_OBJECTIVE,
    },
    loadComponent: () => import("../objectives-view/objectives-view").then((m) => m.ObjectivesViewComponent),
  },
];

const strategicObjectiveRoutes: Route[] = [
  {
    path: "strategic-objective/create",
    canActivate: [notExternalOrParticipatingEntity, ...objectiveGuards.create, NotArchivedPlan()],
    data: {
      title: "breadcrumbs.objectives.strategic_create",
      breadcrumb: "breadcrumbs.objectives.strategic_create",
      pageMode: PageMode.create,
      enabler: AdaaBoolean.N,
      objectiveType: Constants.OBJECTIVE_TYPE.STRATEGIC_OBJECTIVE,
    },
    loadComponent: () => import("../objectives-editor/objectives-editor").then((m) => m.ObjectivesEditorComponent),
  },
  {
    path: "strategic-objective/edit/:id",
    canActivate: [notExternalOrParticipatingEntity, ...objectiveGuards.edit, NotArchivedPlan()],
    data: {
      title: "breadcrumbs.objectives.strategic_edit",
      breadcrumb: "breadcrumbs.objectives.strategic_edit",
      pageMode: PageMode.edit,
      enabler: AdaaBoolean.N,
      objectiveType: Constants.OBJECTIVE_TYPE.STRATEGIC_OBJECTIVE,
    },
    loadComponent: () => import("../objectives-editor/objectives-editor").then((m) => m.ObjectivesEditorComponent),
  },
  {
    path: "strategic-objective/:id",
    data: {
      title: "breadcrumbs.objectives.strategic_view",
      breadcrumb: "breadcrumbs.objectives.strategic_view",
      enabler: AdaaBoolean.N,
      type: Constants.OBJECTIVE_TYPE.STRATEGIC_OBJECTIVE,
    },
    canActivate: [...objectiveGuards.view],
    loadComponent: () => import("../objectives-view/objectives-view").then((m) => m.ObjectivesViewComponent),
  },
];

const mainObjectiveRoutes: Route[] = [
  {
    path: "main-objective/create",
    canActivate: [notExternalOrParticipatingEntity, ...mainObjectiveGuards.create, NotArchivedPlan()],
    data: {
      title: "breadcrumbs.objectives.main_create",
      breadcrumb: "breadcrumbs.objectives.main_create",
      pageMode: PageMode.create,
      enabler: AdaaBoolean.N,
      objectiveType: Constants.OBJECTIVE_TYPE.MAIN_OBJECTIVE,
    },
    loadComponent: () => import("../objectives-editor/objectives-editor").then((m) => m.ObjectivesEditorComponent),
  },
  {
    path: "main-objective/edit/:id",
    canActivate: [notExternalOrParticipatingEntity, ...mainObjectiveGuards.edit, NotArchivedPlan()],
    data: {
      title: "breadcrumbs.objectives.main_edit",
      breadcrumb: "breadcrumbs.objectives.main_edit",
      pageMode: PageMode.edit,
      enabler: AdaaBoolean.N,
      objectiveType: Constants.OBJECTIVE_TYPE.MAIN_OBJECTIVE,
    },
    loadComponent: () => import("../objectives-editor/objectives-editor").then((m) => m.ObjectivesEditorComponent),
  },
  {
    path: "main-objective/:id",
    canActivate: [...mainObjectiveGuards.view],
    data: {
      title: "breadcrumbs.objectives.main_view",
      breadcrumb: "breadcrumbs.objectives.main_view",
      enabler: AdaaBoolean.N,
      type: Constants.OBJECTIVE_TYPE.MAIN_OBJECTIVE,
    },
    loadComponent: () => import("../objectives-view/objectives-view").then((m) => m.ObjectivesViewComponent),
  },
];

/**
 * Entity can only access view enabler objective
 * Entity can only access view transformational objective
 */
const entityViewPmoObjectiveRoutes: Route[] = [
  {
    path: "enabler-objective/:id",
    canActivate: [...objectiveGuards.view, NotArchivedPlan()],
    data: {
      title: "breadcrumbs.enablerobjectives.view",
      breadcrumb: "breadcrumbs.enablerobjectives.view",
      enabler: AdaaBoolean.Y,
      type: Constants.OBJECTIVE_TYPE.ENABLER_OBJECTIVE,
    },
    loadComponent: () => import("../objectives-view/objectives-view").then((m) => m.ObjectivesViewComponent),
  },
  {
    path: "transformational-objective/:id",
    canActivate: [...objectiveGuards.view, NotArchivedPlan()],
    data: {
      title: "breadcrumbs.objectives.transformational_view",
      breadcrumb: "breadcrumbs.objectives.transformational_view",
      enabler: AdaaBoolean.N,
      type: Constants.OBJECTIVE_TYPE.TRANSFORMATIONAL_OBJECTIVE,
    },
    loadComponent: () => import("../objectives-view/objectives-view").then((m) => m.ObjectivesViewComponent),
  },
];

export const entityObjectiveRoutes: Route = {
  path: "objectives",
  canActivate: [
    () => {
      const router = inject(Router);

      return AdaaHelper.isPMOEntity() ? router.createUrlTree(["/console/pmo-objectives"]) : true;
    },
  ],
  children: [
    {
      path: "",
      canActivate: [notExternalOrParticipatingEntity, ...submenuGuards],
      data: {
        title: "breadcrumbs.objectives.objectives",
        breadcrumb: "breadcrumbs.objectives.objectives",
        tableConfList: Constants.MAIN_TABLE_LIST_CONF_KEY.OBJECTIVES,
        enabler: AdaaBoolean.N,
        itemTypeId: Constants.CONSTANT_PERMISSIONS.OBJECTIVES,
      },
      loadComponent: () => import("../objectives-list/objectives-list").then((m) => m.ObjectivesListComponent),
    },
    ...mainObjectiveRoutes,
    ...strategicObjectiveRoutes,
    ...entityViewPmoObjectiveRoutes,
    ...oldNewDSRoute,
  ],
};
